/**
 * Design tokens — colours, gradients, and spacing constants.
 * Use these instead of magic colour strings scattered through inline styles.
 */

/* ─── Brand ─────────────────────────────────────────────────────────────────── */
export const COLORS = {
  brand: {
    primary: '#1B3A6B',
    primaryLight: '#2E5FA3',
    primaryDark: '#0F2647',
    accent: '#0066cc',
  },

  /* ─── Semantic ──────────────────────────────────────────────────────────── */
  success: {
    text: '#155724',
    bg: '#d4edda',
    border: '#c3e6cb',
  },
  warning: {
    text: '#856404',
    bg: '#fff3cd',
    border: '#ffc107',
  },
  danger: {
    text: '#721c24',
    bg: '#f8d7da',
    border: '#f5c6cb',
  },
  info: {
    text: '#0c5460',
    bg: '#d1ecf1',
    border: '#bee5eb',
  },

  /* ─── Neutral ───────────────────────────────────────────────────────────── */
  neutral: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },

  white: '#ffffff',
  black: '#000000',
} as const;

/* ─── Gradients ─────────────────────────────────────────────────────────────── */
export const GRADIENTS = {
  primary: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)',
  primaryDark: 'linear-gradient(135deg, #0F2647 0%, #1B3A6B 100%)',
  success: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
  accent: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
  light: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)',
  subtle: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
} as const;

/* ─── Risk Ratings ──────────────────────────────────────────────────────────── */
export type RiskLevel = 'HIGH' | 'MEDIUM' | 'LOW';

export interface RiskConfig {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
}

export const RISK_RATING: Record<RiskLevel, RiskConfig> = {
  HIGH: {
    label: 'High Risk',
    color: '#721c24',
    bg: '#f8d7da',
    border: '#f5c6cb',
    icon: '🔴',
  },
  MEDIUM: {
    label: 'Medium Risk',
    color: '#856404',
    bg: '#fff3cd',
    border: '#ffc107',
    icon: '🟡',
  },
  LOW: {
    label: 'Low Risk',
    color: '#155724',
    bg: '#d4edda',
    border: '#c3e6cb',
    icon: '🟢',
  },
} as const;

/* ─── Spacing / Radius ──────────────────────────────────────────────────────── */
export const RADIUS = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  pill: '9999px',
} as const;

export const SHADOW = {
  sm: '0 1px 3px rgba(0,0,0,0.08)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 4px 24px rgba(0,0,0,0.06)',
  xl: '0 8px 32px rgba(0,0,0,0.1)',
} as const;
